import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import type { DocumentProps } from '@react-pdf/renderer';
import type { MealFrontend, MealPlanFrontend } from '@/types/mealPlan';
import { mapMealPlanToPDF, mapMealToPDF } from '@/components/pdf/utils/mappers';

interface UsePDFExportResult {
  exportMealPlan: (mealPlan: MealPlanFrontend, filename?: string) => Promise<void>;
  exportMeal: (meal: MealFrontend, filename?: string) => Promise<void>;
  isGenerating: boolean;
}

const triggerDownload = (blob: Blob, filename: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const sanitizeFilename = (value: string): string => {
  return value.replace(/[^a-z0-9-]+/gi, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
};

const sanitizeDayLabel = (value: string): string => {
  const cleaned = value.replace(/[^a-z0-9\s]/gi, ' ').replace(/\s+/g, ' ').trim();
  return cleaned.length > 0 ? cleaned : 'Day 1';
};

const formatMealTypeLabel = (type?: MealFrontend['mealType']): string | undefined => {
  if (!type) {
    return undefined;
  }

  return type
    .split('_')
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
};

const resolveDayNumber = (meal: MealFrontend): number | undefined => {
  const explicitDayNumber = (meal as MealFrontend & { dayNumber?: number }).dayNumber;
  if (typeof explicitDayNumber === 'number' && Number.isFinite(explicitDayNumber) && explicitDayNumber > 0) {
    return explicitDayNumber;
  }

  const dayLabel = (meal as MealFrontend & { dayLabel?: string }).dayLabel;
  const labelMatch = dayLabel?.match(/\d+/);
  if (labelMatch && labelMatch[0]) {
    return Number(labelMatch[0]);
  }

  const tagDigit =
    meal.tags?.map((tag) => tag.match(/\d+/)?.[0]).find((digit) => typeof digit === 'string') ?? null;
  if (tagDigit) {
    return Number(tagDigit);
  }

  return undefined;
};

export const usePDFExport = (): UsePDFExportResult => {
  const [isGenerating, setIsGenerating] = useState(false);

  const renderDocument = useCallback(async (node: ReactElement<DocumentProps>, filename: string) => {
    setIsGenerating(true);
    try {
      const [{ pdf }] = await Promise.all([import('@react-pdf/renderer')]);
      const blob = await pdf(node).toBlob();
      triggerDownload(blob, filename);
    } finally {
      setIsGenerating(false);
    }
  }, []);

  const exportMealPlan = useCallback(
    async (mealPlan: MealPlanFrontend, filename?: string) => {
      const [{ MealPlanPDF }] = await Promise.all([import('@/components/pdf/documents/MealPlanPDF')]);
      const pdfData = mapMealPlanToPDF(mealPlan);
      const safeFilename =
        filename ||
        `BiteWise - ${sanitizeFilename(mealPlan.label || 'MealPlan') || 'MealPlan'}-${new Date().toISOString().split('T')[0]}.pdf`;

      await renderDocument(<MealPlanPDF data={pdfData} />, safeFilename);
    },
    [renderDocument],
  );

  const exportMeal = useCallback(
    async (meal: MealFrontend, filename?: string) => {
      const pdfData = mapMealToPDF(meal);
      const dayNumber = resolveDayNumber(meal) ?? 1;
      const dayLabel = sanitizeDayLabel(`Day ${dayNumber}`);
      const mealTypeLabel = formatMealTypeLabel(meal.mealType) || 'Meal';
      const formattedDate = new Date().toISOString().split('T')[0];
      const safeDaySegment = dayLabel;
      const safeTypeSegment = sanitizeFilename(mealTypeLabel) || 'Meal';
      const safeNameSegment = sanitizeFilename(meal.mealName || 'Meal') || 'Meal';
      const safeFilename =
        filename || `BiteWise - ${safeDaySegment} - ${safeTypeSegment} - ${safeNameSegment} - ${formattedDate}.pdf`;
      const [{ MealPDF }] = await Promise.all([import('@/components/pdf/documents/MealPDF')]);

      await renderDocument(<MealPDF meal={pdfData} />, safeFilename);
    },
    [renderDocument],
  );

  return {
    exportMealPlan,
    exportMeal,
    isGenerating,
  };
};

export default usePDFExport;

